import { Alert, Container, Snackbar, Typography } from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import dayjs, { Dayjs } from 'dayjs'
import {
  selectCourseByID,
  selectSelectedCourse,
  selectStartDate,
  selectEndDate,
  selectSelectedLicenseTypes,
  startDateChanged,
  endDateChanged,
  selectedLicenseTypesChanged,
  SentimentAnalysisResponse,
  JobStatusResponse,
} from 'src/v3/scribe/scribe.slice'
import ScribeFilters from './filters'
import SentimentAnalysisModal from './sentiment-analysis-modal'
import AnalyticsCharts from './analytics-charts'
import LiveClassAttendance from './live-class-attendance'

type LicenseType = 'Nurse' | 'EMT' | 'Paramedic' | 'AEMT' | 'Other'

export default () => {
  let dispatch = useAppDispatch()

  let startDate = useAppSelector(selectStartDate)
  let endDate = useAppSelector(selectEndDate)
  let selectedLicenseTypes = useAppSelector(selectSelectedLicenseTypes)
  let selectedLicenseType =
    selectedLicenseTypes.length > 0 ? selectedLicenseTypes[0] : null

  let selectedCourse = useAppSelector(selectSelectedCourse)
  let course = useAppSelector(selectCourseByID(selectedCourse))

  let [errorMessage, setErrorMessage] = useState<string>('')

  // Local sentiment analysis state
  let [sentimentAnalysis, setSentimentAnalysis] = useState<string | null>(null)
  let [sentimentAnalysisLoading, setSentimentAnalysisLoading] = useState(false)
  let [sentimentAnalysisError, setSentimentAnalysisError] = useState<
    string | null
  >(null)
  let [sentimentModalOpen, setSentimentModalOpen] = useState(false)

  // Polling state
  let [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null)
  let [isPolling, setIsPolling] = useState<boolean>(false)
  let [lastProgressCount, setLastProgressCount] = useState<number>(0)

  let pollingInterval = useRef<number | NodeJS.Timeout | undefined>(undefined)

  let handleStartDateChange = (date: Dayjs | null) => {
    dispatch(startDateChanged(date ? date.format('YYYY-MM-DD') : null))
  }

  let handleEndDateChange = (date: Dayjs | null) => {
    dispatch(endDateChanged(date ? date.format('YYYY-MM-DD') : null))
  }

  let handleLicenseTypeChange = (type: LicenseType | null) => {
    dispatch(selectedLicenseTypesChanged(type ? [type] : []))
    // Clear sentiment analysis when filters change
    setSentimentAnalysis(null)
    setSentimentAnalysisError(null)
  }

  // Clear sentiment analysis when course or dates change
  useEffect(() => {
    setSentimentAnalysis(null)
    setSentimentAnalysisError(null)
    setJobStatus(null)
    setIsPolling(false)
    setLastProgressCount(0)
  }, [course?.id, startDate, endDate])

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current)
      }
    },
    [],
  )

  let startPolling = (correlationIDToPoll: string): void => {
    setIsPolling(true)
    setLastProgressCount(0)

    let pollJobStatus = async (): Promise<void> => {
      try {
        let response = await axios.get<JobStatusResponse>(
          `/api/mother/sessions/${correlationIDToPoll}/status`,
        )
        setJobStatus(response.data)

        // Stop polling when complete
        if (
          response.data.status === 'sessionCompleted' ||
          response.data.status === 'sessionFailed'
        ) {
          setIsPolling(false)
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current)
          }

          // Set the final result if available
          if (response.data.result) {
            setSentimentAnalysis(response.data.result.response)
          }
        }

        // Update progress count for timeline
        if (response.data.progress.length > lastProgressCount) {
          setLastProgressCount(response.data.progress.length)
        }
      } catch (err: any) {
        setSentimentAnalysisError(
          err.response?.data?.message || 'Failed to poll job status',
        )
        setIsPolling(false)
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current)
        }
      }
    }

    // Initial poll
    pollJobStatus()

    // Set up polling interval
    // eslint-disable-next-line fp/no-mutation
    pollingInterval.current = setInterval(pollJobStatus, 3000)
  }

  let generateSentimentAnalysis = async () => {
    // Detailed validation with specific error messages
    if (!course) {
      setErrorMessage('Please select a course to generate sentiment analysis')
      return
    }

    if (!startDate || !endDate) {
      setErrorMessage(
        'Please select both start and end dates for the analysis period',
      )
      return
    }

    if (!selectedLicenseType) {
      setErrorMessage('Please select a license type for sentiment analysis')
      return
    }

    // Check if date range is valid
    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      setErrorMessage('End date must be after start date')
      return
    }

    // Open modal immediately and start loading
    setSentimentModalOpen(true)
    setSentimentAnalysisLoading(true)
    setSentimentAnalysisError(null)
    setSentimentAnalysis(null)

    try {
      let request = {
        courseID: course.id,
        licenseType: selectedLicenseType,
        startDate: dayjs(startDate).startOf('day').toISOString(),
        endDate: dayjs(endDate).endOf('day').toISOString(),
      }

      let response = await axios.post<SentimentAnalysisResponse>(
        '/api/mother/demo/sentiment-analysis',
        request,
      )

      // Start polling for job status
      setTimeout(() => {
        setSentimentAnalysisLoading(false)
        startPolling(response.data.correlationID)
      }, 1000)
    } catch (error: any) {
      let errorMsg =
        error.response?.status === 404
          ? 'No survey data found for the specified course, license group, and date range'
          : error.response?.status === 403
            ? 'You do not have permission to access sentiment analysis'
            : error.response?.status === 400
              ? error.response?.data?.error || 'Invalid request parameters'
              : error.response?.data?.error ||
                'Failed to generate sentiment analysis'

      setSentimentAnalysisError(errorMsg)
      setSentimentAnalysisLoading(false)
    }
  }

  return (
    <>
      <Container maxWidth="xl">
        <Typography variant="h1" sx={{ py: 4 }}>
          Accreditation Reporting
        </Typography>

        {/* New Dashboard Filters */}
        <ScribeFilters
          startDate={startDate ? dayjs(startDate) : null}
          endDate={endDate ? dayjs(endDate) : null}
          licenseType={selectedLicenseType}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onLicenseTypeChange={handleLicenseTypeChange}
          onGenerateSentimentAnalysis={generateSentimentAnalysis}
          sentimentAnalysisLoading={sentimentAnalysisLoading}
          selectedCourse={course}
        />

        {/* Analytics Dashboard */}
        <AnalyticsCharts
          course={course}
          licenseType={selectedLicenseType}
          startDate={startDate}
          endDate={endDate}
        />

        {/* Live Class Attendance Section - Only show for Live courses */}
        {course?.kind === 'Live' &&
          course.courseCode &&
          startDate &&
          endDate &&
          selectedLicenseType && (
            <LiveClassAttendance
              courseCode={course.courseCode}
              startDate={startDate}
              endDate={endDate}
              licenseGroup={selectedLicenseType}
            />
          )}

        <SentimentAnalysisModal
          open={sentimentModalOpen}
          onClose={() => setSentimentModalOpen(false)}
          isLoading={sentimentAnalysisLoading}
          analysis={sentimentAnalysis}
          error={sentimentAnalysisError}
          courseName={course?.name || null}
          licenseGroup={selectedLicenseType}
          startDate={startDate}
          endDate={endDate}
          jobStatus={jobStatus}
          isPolling={isPolling}
        />
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
